import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { corporateInput, fmtMoney } from "@/components/Shared";
import {
  MapPin, Lightning, User as UserIcon, Calendar, Tag, CheckCircle,
  PaperPlaneTilt, ArrowRight, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { BusinessDatePicker } from "@/components/BusinessDatePicker";

const initialForm = {
  first_name: "", last_name: "",
  email: "", phone: "",
  address_line: "", unit: "", city: "", state: "Texas", postcode: "",
  plan_id: "",
  desired_start_date: "",
  referral_code: "",
  notes: "",
};

function StepHeader({ n, label, active, done }) {
  return (
    <div className={`flex items-center gap-3 ${active ? "" : "opacity-50"}`}>
      <div className={`w-8 h-8 rounded-full grid place-items-center text-sm font-bold transition-colors ${done ? "bg-emerald-500 text-white" : active ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>
        {done ? <CheckCircle size={16} weight="fill" /> : n}
      </div>
      <div className="text-sm font-semibold text-slate-700">{label}</div>
    </div>
  );
}

export default function NewConnectionPage() {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  // --- address autocomplete ---
  const [addrQuery, setAddrQuery] = useState("");
  const [addrResults, setAddrResults] = useState([]);
  const [addrLoading, setAddrLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!addrQuery || addrQuery.length < 4) { setAddrResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const { data } = await api.get("/address-search", { params: { q: addrQuery } });
        setAddrResults(data);
      } catch (err) { console.warn("[address-search] failed:", err?.message); }
      finally { setAddrLoading(false); }
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [addrQuery]);

  const pickAddress = (a) => {
    setForm(f => ({
      ...f,
      address_line: a.line1,
      // Preserve apartment if user already typed one; otherwise keep empty.
      unit: f.unit,
      city: a.city,
      state: a.state || "Texas",
      postcode: a.postcode || "",
    }));
    setAddrResults([]);
    setAddrQuery(a.display);
  };

  // --- plans ---
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/orders/plans-public").then(r => setPlans(r.data)).catch(() => {}); }, []);
  const selectedPlan = plans.find(p => p.id === form.plan_id);

  // --- referral lookup ---
  const [referrer, setReferrer] = useState(null);
  const [refLookupErr, setRefLookupErr] = useState("");
  const refDebounceRef = useRef(null);

  useEffect(() => {
    if (!form.referral_code) { setReferrer(null); setRefLookupErr(""); return; }
    if (refDebounceRef.current) clearTimeout(refDebounceRef.current);
    refDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients/by-referral-code/${form.referral_code.trim().toUpperCase()}`);
        setReferrer(data); setRefLookupErr("");
      } catch (e) {
        setReferrer(null);
        setRefLookupErr(e.response?.status === 404 ? "Código no encontrado" : "Error consultando código");
      }
    }, 500);
    return () => refDebounceRef.current && clearTimeout(refDebounceRef.current);
  }, [form.referral_code]);

  // --- step gating ---
  const canStep2 = !!(form.address_line && form.city);
  const canStep3 = canStep2 && !!form.plan_id;
  const canStep4 = canStep3 && !!form.first_name && !!form.last_name && form.phone.replace(/\D/g, "").length === 10;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Append apt / unit / suite to the main address line so the backend and
      // downstream REPs see one complete address.
      if (payload.unit && payload.unit.trim()) {
        const u = payload.unit.trim();
        const prefix = /^(#|apt|unit|ste|suite)/i.test(u) ? "" : "#";
        payload.address_line = `${payload.address_line} ${prefix}${u}`.trim();
      }
      delete payload.unit;
      if (!payload.email) delete payload.email;
      if (!payload.desired_start_date) delete payload.desired_start_date;
      if (!payload.referral_code) delete payload.referral_code;
      if (!payload.notes) delete payload.notes;
      const { data } = await api.post("/orders/connection-request", payload);
      setSuccess(data);
      toast.success(`Orden #${data.order_number} enviada a Service`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const reset = () => { setForm(initialForm); setStep(1); setAddrQuery(""); setReferrer(null); setSuccess(null); };

  if (success) {
    return (
      <PageShell section="Operación" page="Nueva conexión" context="Confirmación" testid="nc-success">
        <div className="max-w-2xl mx-auto bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-emerald-400 to-emerald-600" />
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-4">
              <CheckCircle size={36} weight="fill" className="text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900">Solicitud enviada a Service</h2>
            <p className="text-sm text-slate-500 mt-2">El equipo recibirá la nueva orden en su backlog y procederá a procesarla.</p>
            <div className="mt-6 inline-block bg-slate-50 border border-slate-200 rounded-lg px-6 py-4 text-left">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Número de orden</div>
              <div className="text-xl font-mono font-bold text-slate-900 mt-1">#{success.order_number}</div>
              <div className="text-xs text-slate-600 mt-3 space-y-0.5">
                <div><span className="text-slate-400">Cliente:</span> {success.order.client_name}</div>
                <div><span className="text-slate-400">Plan:</span> {success.order.plan_name}</div>
                <div><span className="text-slate-400">Dirección:</span> {success.order.address}</div>
                {success.order.referrer_name && <div><span className="text-slate-400">Referido por:</span> {success.order.referrer_name}</div>}
              </div>
            </div>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button onClick={reset} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="nc-new-another"><PaperPlaneTilt size={15} className="mr-1.5" /> Crear otra solicitud</Button>
              <a href="/comunicacion" className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1">Ver en comunicaciones <ArrowRight size={12} /></a>
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      section="Operación"
      page="Nueva conexión"
      context="Solicitud para Service"
      testid="nc-toolbar"
    >
      {/* Stepper */}
      <div className="max-w-3xl mx-auto bg-white border border-slate-200 rounded-xl p-3 sm:p-4 mb-4 flex items-center justify-between gap-1 sm:gap-2 overflow-x-auto">
        <StepHeader n={1} label="Dirección" active={step >= 1} done={canStep2 && step > 1} />
        <ArrowRight size={14} className="text-slate-300 shrink-0" />
        <StepHeader n={2} label="Plan" active={step >= 2} done={canStep3 && step > 2} />
        <ArrowRight size={14} className="text-slate-300 shrink-0" />
        <StepHeader n={3} label="Cliente" active={step >= 3} done={canStep4 && step > 3} />
        <ArrowRight size={14} className="text-slate-300 shrink-0" />
        <StepHeader n={4} label="Referido y envío" active={step >= 4} done={false} />
      </div>

      {/* Step 1 — Address */}
      {step === 1 && (
        <div className="max-w-3xl mx-auto">
          <div className="max-w-2xl mx-auto bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="nc-step1-card">
            {/* Header band */}
            <div className="relative px-5 sm:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-amber-500" />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <MapPin size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Paso 1 · Ubicación</div>
                  <h3 className="text-lg font-semibold text-slate-900 mt-0.5">Buscar dirección en Texas</h3>
                  <p className="text-xs text-slate-500 mt-1">Empieza a escribir la dirección. Resultados filtrados a Texas.</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-8">
              {/* Search field */}
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={addrQuery}
                    onChange={(e) => setAddrQuery(e.target.value)}
                    placeholder="ej. 1600 Pennsylvania Ave Austin"
                    className="pl-10 h-12 text-sm border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                    data-testid="nc-addr-search"
                  />
                  {addrLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 animate-pulse">buscando…</div>}
                </div>
                {addrResults.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-80 overflow-y-auto" data-testid="nc-addr-results">
                    {addrResults.map((a, idx) => (
                      <button key={idx} onClick={() => pickAddress(a)} className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm border-b border-slate-100 last:border-0" data-testid={`nc-addr-pick-${idx}`}>
                        <div className="font-medium text-slate-900">{a.line1}</div>
                        <div className="text-xs text-slate-500">{a.city}, {a.state} {a.postcode}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-100" />
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">o edita manualmente</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>

              {/* Manual fields */}
              <div className="grid grid-cols-6 gap-4">
                <div className="col-span-6 sm:col-span-4">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Dirección *</Label>
                  <Input value={form.address_line} onChange={(e) => setForm({...form, address_line: e.target.value})} className={corporateInput + " h-11"} data-testid="nc-line1" placeholder="Calle y número" />
                </div>
                <div className="col-span-6 sm:col-span-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Apto / Unit / Suite</Label>
                  <Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} className={corporateInput + " h-11 font-mono"} data-testid="nc-unit" placeholder="#304, Apt B, Suite 12…" />
                </div>
                <div className="col-span-4">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Ciudad *</Label>
                  <Input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className={corporateInput + " h-11"} data-testid="nc-city" placeholder="Austin, Houston…" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Código postal</Label>
                  <Input value={form.postcode} onChange={(e) => setForm({...form, postcode: e.target.value})} className={corporateInput + " h-11 font-mono tabular-nums"} data-testid="nc-zip" placeholder="00000" />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-slate-500 order-2 sm:order-1">
                {canStep2 ? <span className="text-emerald-700 font-medium">✓ Dirección lista</span> : <span>Completa dirección y ciudad</span>}
              </div>
              <Button onClick={() => setStep(2)} disabled={!canStep2} className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-5 ml-auto order-1 sm:order-2" data-testid="nc-next-1">
                Continuar <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2 — Plan */}
      {step === 2 && (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="nc-step2-card">
            {/* Header band */}
            <div className="relative px-5 sm:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-amber-500" />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <Lightning size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Paso 2 · Plan</div>
                  <h3 className="text-lg font-semibold text-slate-900 mt-0.5">Selecciona un plan</h3>
                  <p className="text-xs text-slate-500 mt-1">Planes activos del catálogo de FLEX ENERGY.</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-8">
              {plans.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-10">Sin planes activos. Pide al admin de Support que cree uno en <span className="font-mono">/support/plans</span>.</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="nc-plans">
                  {plans.map(p => {
                    const sel = form.plan_id === p.id;
                    const isFixed = p.type === "fixed";
                    return (
                      <button
                        key={p.id}
                        onClick={() => setForm({...form, plan_id: p.id})}
                        data-testid={`nc-plan-${p.id}`}
                        className={`group relative text-left rounded-xl border overflow-hidden transition-all duration-200 ${
                          sel
                            ? "border-slate-900 shadow-[0_8px_28px_-6px_rgba(15,23,42,0.25)] -translate-y-0.5"
                            : "border-slate-200 hover:border-slate-400 hover:shadow-md hover:-translate-y-0.5"
                        }`}
                      >
                        {/* Top accent bar */}
                        <div className={`h-1 ${sel ? "bg-slate-900" : isFixed ? "bg-emerald-400" : "bg-amber-400"} transition-colors`} />

                        {/* Selected check */}
                        {sel && (
                          <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-900 text-white grid place-items-center shadow-sm">
                            <CheckCircle size={14} weight="fill" />
                          </span>
                        )}

                        <div className="p-5">
                          {/* REP + type pill */}
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">{p.rep_name || "FLEX ENERGY"}</div>
                            <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                              isFixed
                                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                : "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                            }`}>
                              {isFixed ? "Tarifa fija" : "Tarifa variable"}
                            </span>
                          </div>

                          {/* Plan name */}
                          <div className="text-xl font-bold text-slate-900 tracking-tight pr-7">{p.name}</div>

                          {/* Price hero */}
                          <div className="mt-4 flex items-baseline gap-1.5">
                            <span className="text-3xl font-bold font-mono tabular-nums text-slate-900">{Number(p.kwh_rate).toFixed(2)}</span>
                            <span className="text-sm font-semibold text-slate-500">¢ / kWh</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">Tarifa por kilovatio-hora</div>

                          {/* Divider */}
                          <div className="my-4 border-t border-slate-100" />

                          {/* Key metrics */}
                          <ul className="space-y-2 text-sm">
                            <li className="flex items-center justify-between">
                              <span className="text-slate-500 flex items-center gap-1.5">
                                <CheckCircle size={13} weight="fill" className="text-slate-400" />
                                Consumo promedio
                              </span>
                              <span className="font-semibold font-mono tabular-nums text-slate-900">{fmtMoney(p.monthly_avg)}/mes</span>
                            </li>
                            <li className="flex items-center justify-between">
                              <span className="text-slate-500 flex items-center gap-1.5">
                                <CheckCircle size={13} weight="fill" className="text-slate-400" />
                                Término del contrato
                              </span>
                              <span className="font-semibold font-mono tabular-nums text-slate-900">{p.term_months} meses</span>
                            </li>
                            {(p.commission_amount > 0 || p.commission_pct > 0) && (
                              <li className="flex items-center justify-between">
                                <span className="text-slate-500 flex items-center gap-1.5">
                                  <Tag size={13} weight="fill" className="text-slate-400" />
                                  Bono referido
                                </span>
                                <span className="font-semibold font-mono tabular-nums text-slate-900">
                                  {p.commission_amount > 0 && `$${p.commission_amount}`}
                                  {p.commission_amount > 0 && p.commission_pct > 0 && " · "}
                                  {p.commission_pct > 0 && `${p.commission_pct}%`}
                                </span>
                              </li>
                            )}
                          </ul>

                          {/* Footer tag */}
                          <div className={`mt-5 pt-3 border-t border-slate-100 text-[11px] font-semibold flex items-center justify-between ${sel ? "text-slate-900" : "text-slate-400 group-hover:text-slate-600"}`}>
                            <span className="uppercase tracking-wider">{sel ? "Plan seleccionado" : "Elegir este plan"}</span>
                            <ArrowRight size={13} className={`transition-transform ${sel ? "" : "group-hover:translate-x-0.5"}`} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between gap-3 flex-wrap">
              <Button variant="outline" onClick={() => setStep(1)} className="h-10">Atrás</Button>
              <div className="flex items-center gap-3 ml-auto">
                <div className="text-[11px] text-slate-500 hidden sm:block">
                  {canStep3 ? <span className="text-emerald-700 font-medium">✓ Plan seleccionado</span> : <span>Selecciona un plan</span>}
                </div>
                <Button onClick={() => setStep(3)} disabled={!canStep3} className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-5" data-testid="nc-next-2">
                  Continuar <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3 — Customer */}
      {step === 3 && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="nc-step3-card">
            {/* Header band */}
            <div className="relative px-5 sm:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-amber-500" />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <UserIcon size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Paso 3 · Cliente</div>
                  <h3 className="text-lg font-semibold text-slate-900 mt-0.5">Información del cliente</h3>
                  <p className="text-xs text-slate-500 mt-1">Datos para que Service contacte y active el servicio.</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nombre *</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} className={corporateInput + " h-11"} data-testid="nc-first" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Apellido *</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} className={corporateInput + " h-11"} data-testid="nc-last" />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Teléfono *</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => {
                      // Keep only digits, cap at 10 (US phone), format as XXX-XXX-XXXX for readability.
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      let formatted = digits;
                      if (digits.length > 6) formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
                      else if (digits.length > 3) formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
                      setForm({ ...form, phone: formatted });
                    }}
                    inputMode="numeric"
                    maxLength={12}
                    className={corporateInput + " h-11 font-mono"}
                    data-testid="nc-phone"
                    placeholder="512-555-1234"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">10 dígitos (sin código de país).</p>
                </div>
                <div>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={corporateInput + " h-11"} data-testid="nc-email" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1">
                    <Calendar size={11} /> Fecha deseada de inicio
                  </Label>
                  <BusinessDatePicker
                    value={form.desired_start_date}
                    onChange={(iso) => setForm({ ...form, desired_start_date: iso })}
                    minDate={new Date().toISOString().slice(0, 10)}
                    data-testid="nc-start"
                  />
                  <p className="text-[10px] text-slate-500 mt-1.5 leading-relaxed">
                    No se agenda en feriados US ni domingos. Los días marcados con punto rojo son feriados observados.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between gap-3 flex-wrap">
              <Button variant="outline" onClick={() => setStep(2)} className="h-10">Atrás</Button>
              <div className="flex items-center gap-3 ml-auto">
                <div className="text-[11px] text-slate-500 hidden sm:block">
                  {canStep4 ? <span className="text-emerald-700 font-medium">✓ Datos completos</span> : <span>Nombre, apellido y teléfono (10 dígitos)</span>}
                </div>
                <Button onClick={() => setStep(4)} disabled={!canStep4} className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-5" data-testid="nc-next-3">
                  Continuar <ArrowRight size={14} className="ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 4 — Referral + submit */}
      {step === 4 && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="nc-step4-card">
            {/* Header band */}
            <div className="relative px-5 sm:px-8 pt-6 pb-5 border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-amber-500" />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                  <Tag size={18} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Paso 4 · Referido y envío</div>
                  <h3 className="text-lg font-semibold text-slate-900 mt-0.5">Código de referido (opcional)</h3>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">Si el cliente fue referido por otro, ingresa su código <span className="font-mono">FLEX-XXXXXX</span>. La comisión se calcula automáticamente al activar el servicio.</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 sm:p-8">
              <div className="relative">
                <Input value={form.referral_code} onChange={(e) => setForm({...form, referral_code: e.target.value.toUpperCase()})} placeholder="FLEX-XXXXXX" className={corporateInput + " uppercase font-mono tracking-wider h-11"} data-testid="nc-ref-code" />
                {form.referral_code && (
                  <button onClick={() => setForm({...form, referral_code: ""})} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"><X size={14} /></button>
                )}
              </div>
              {referrer && (
                <div className="mt-3 flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm" data-testid="nc-ref-valid">
                  <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" />
                  <span className="text-emerald-900"><strong>Referido válido:</strong> {referrer.name}</span>
                </div>
              )}
              {refLookupErr && (
                <div className="mt-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="nc-ref-error">
                  <X size={16} weight="bold" className="text-red-500 shrink-0" />
                  {refLookupErr}
                </div>
              )}

              <div className="mt-5">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notas para Service (opcional)</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" data-testid="nc-notes" />
              </div>

              {/* Summary preview */}
              <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">Resumen</div>
                <div className="text-sm space-y-1 break-words">
                  <div><span className="text-slate-500">Cliente:</span> <strong>{form.first_name} {form.last_name}</strong></div>
                  <div><span className="text-slate-500">Tel:</span> {form.phone} · <span className="text-slate-500">Email:</span> {form.email || "—"}</div>
                  <div><span className="text-slate-500">Dirección:</span> {form.address_line}, {form.city}, TX {form.postcode}</div>
                  {selectedPlan && <div><span className="text-slate-500">Plan:</span> <strong>{selectedPlan.name}</strong> · {selectedPlan.rep_name} · {fmtMoney(selectedPlan.monthly_avg)}/mes</div>}
                  {form.desired_start_date && <div><span className="text-slate-500">Inicio deseado:</span> {form.desired_start_date}</div>}
                  {referrer && <div><span className="text-slate-500">Referido por:</span> <strong>{referrer.name}</strong></div>}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-5 sm:px-8 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between gap-3 flex-wrap">
              <Button variant="outline" onClick={() => setStep(3)} disabled={submitting} className="h-10">Atrás</Button>
              <Button onClick={submit} disabled={submitting || (form.referral_code && !referrer)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-5 ml-auto" data-testid="nc-submit">
                {submitting ? "Enviando…" : <><PaperPlaneTilt size={14} weight="fill" className="mr-1.5" /> Enviar a Service</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
